/**
 * `measureController.ts` — WP-6 (3D model / BIM / 3D Tiles ToolCard).
 *
 * Three.js raycaster + scene-overlay primitives extracted from the
 * legacy `Giro3DToolbar.tsx` so the slim in-canvas toolbar (post-WP-6)
 * keeps measurement behaviour while still hitting the ≤ 12 KB size
 * budget mandated by the masterplan §8.6.
 *
 * The exports are imperative on purpose: each call manipulates the live
 * Three.js scene attached to the engine instance. The slim toolbar
 * holds React state for the measurement results / cursor snap and
 * delegates every per-frame side-effect to this controller.
 */

import { tokens } from '@/design';

const MEASURE_MARKER_RADIUS = 0.15;
const MEASURE_RENDER_ORDER = 999;
const VERTEX_SNAP_THRESHOLD = 0.5;
const VERTEX_CHECK_LIMIT = 5000;

const MEASURE_COLORS = {
  snap: tokenColorToThree(tokens.color.semantic.success),
  unsnapped: tokenColorToThree(tokens.color.semantic.danger),
  line: tokenColorToThree(tokens.color.semantic.warning),
};

function tokenColorToThree(color: string): number {
  const normalized = color.startsWith('#') ? color.slice(1) : color;
  const parsed = Number.parseInt(normalized, 16);
  return Number.isFinite(parsed)
    ? parsed
    : Number.parseInt(tokens.color.accent.primary.slice(1), 16);
}

export interface MeasureRaycaster {
  THREE: any;
  raycaster: any;
}

export interface MeasureSurfaces {
  scene: any;
  camera: any;
  domEl: HTMLElement;
  notifyChange?: () => void;
}

export type MeasurePoint3 = readonly [number, number, number];

export interface MeasureRaycastResult {
  readonly point: MeasurePoint3;
  readonly snapped: boolean;
}

let cachedRaycaster: MeasureRaycaster | null = null;

export async function getRaycaster(): Promise<MeasureRaycaster> {
  if (cachedRaycaster) return cachedRaycaster;
  const THREE = await import('three');
  (window as any).__THREE__ = THREE;
  cachedRaycaster = { THREE, raycaster: new THREE.Raycaster() };
  return cachedRaycaster;
}

/**
 * Raycast a screen event against the Three.js scene meshes and
 * vertex-snap to the nearest geometry vertex when within the snap
 * threshold. Returns `null` when there is no hit.
 */
export async function raycastAtEvent(
  surfaces: MeasureSurfaces | null,
  event: MouseEvent,
): Promise<MeasureRaycastResult | null> {
  if (!surfaces?.camera || !surfaces.domEl) return null;
  const { THREE, raycaster } = await getRaycaster();
  const rect = surfaces.domEl.getBoundingClientRect();
  const mouse = new THREE.Vector2(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -((event.clientY - rect.top) / rect.height) * 2 + 1,
  );
  raycaster.setFromCamera(mouse, surfaces.camera);

  const meshes: any[] = [];
  surfaces.scene?.traverse((child: any) => {
    if (child.isMesh && child.geometry && child.visible) meshes.push(child);
  });

  const intersects = raycaster.intersectObjects(meshes, false);
  if (!intersects || intersects.length === 0) return null;

  const hit = intersects[0];
  const hitPoint = hit.point;
  let snapped = false;
  let finalPoint: [number, number, number] = [hitPoint.x, hitPoint.y, hitPoint.z];

  const geom = hit.object?.geometry;
  if (geom?.attributes?.position) {
    const posAttr = geom.attributes.position;
    const worldMatrix = hit.object.matrixWorld;
    let closestDist = VERTEX_SNAP_THRESHOLD;
    const tempVec = new THREE.Vector3();
    const checkCount = Math.min(posAttr.count, VERTEX_CHECK_LIMIT);
    for (let i = 0; i < checkCount; i++) {
      tempVec.set(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i));
      tempVec.applyMatrix4(worldMatrix);
      const d = tempVec.distanceTo(hitPoint);
      if (d < closestDist) {
        closestDist = d;
        finalPoint = [tempVec.x, tempVec.y, tempVec.z];
        snapped = true;
      }
    }
  }
  return { point: finalPoint, snapped };
}

/** Tracker for live measurement scene overlays so the toolbar can clean up cleanly. */
export interface MeasureOverlayTracker {
  markers: any[];
  line: any | null;
}

export function createOverlayTracker(): MeasureOverlayTracker {
  return { markers: [], line: null };
}

export async function clearMeasureOverlays(
  tracker: MeasureOverlayTracker,
  surfaces: MeasureSurfaces | null,
): Promise<void> {
  if (!surfaces?.scene) return;
  for (const marker of tracker.markers) {
    surfaces.scene.remove(marker);
    marker.geometry?.dispose?.();
    marker.material?.dispose?.();
  }
  tracker.markers = [];
  if (tracker.line) {
    surfaces.scene.remove(tracker.line);
    tracker.line.geometry?.dispose?.();
    tracker.line.material?.dispose?.();
    tracker.line = null;
  }
  surfaces.notifyChange?.();
}

export async function addMarkerAtPoint(
  tracker: MeasureOverlayTracker,
  surfaces: MeasureSurfaces | null,
  point: MeasurePoint3,
  snapped: boolean,
): Promise<void> {
  if (!surfaces?.scene) return;
  const { THREE } = await getRaycaster();
  const color = snapped ? MEASURE_COLORS.snap : MEASURE_COLORS.unsnapped;
  const geo = new THREE.SphereGeometry(MEASURE_MARKER_RADIUS, 16, 16);
  const mat = new THREE.MeshBasicMaterial({
    color,
    depthTest: false,
    transparent: true,
    opacity: 0.9,
  });
  const sphere = new THREE.Mesh(geo, mat);
  sphere.position.set(point[0], point[1], point[2]);
  sphere.renderOrder = MEASURE_RENDER_ORDER;
  surfaces.scene.add(sphere);
  tracker.markers.push(sphere);
  surfaces.notifyChange?.();
}

export async function drawLineBetween(
  tracker: MeasureOverlayTracker,
  surfaces: MeasureSurfaces | null,
  p1: MeasurePoint3,
  p2: MeasurePoint3,
): Promise<void> {
  if (!surfaces?.scene) return;
  const { THREE } = await getRaycaster();
  if (tracker.line) {
    surfaces.scene.remove(tracker.line);
    tracker.line.geometry?.dispose?.();
    tracker.line.material?.dispose?.();
  }
  const geo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(...p1),
    new THREE.Vector3(...p2),
  ]);
  const mat = new THREE.LineBasicMaterial({
    color: MEASURE_COLORS.line,
    linewidth: tokens.border.width.thin,
    depthTest: false,
  });
  const line = new THREE.Line(geo, mat);
  line.renderOrder = MEASURE_RENDER_ORDER;
  surfaces.scene.add(line);
  tracker.line = line;
  surfaces.notifyChange?.();
}

/** Format a metres distance using the GeoViewer convention (km / m / cm). */
export function formatMeasureDistance(m: number): string {
  if (m >= 1000) return `${(m / 1000).toFixed(2)} km`;
  if (m >= 1) return `${m.toFixed(2)} m`;
  return `${(m * 100).toFixed(1)} cm`;
}
